import React, { useCallback, useRef, useEffect, useReducer, useContext } from 'react';
import AtlaskitTooltip from '@atlaskit/tooltip';
import { CartesianViewBox } from 'recharts/types/util/types';
import { css } from '@emotion/core';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelProps,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  YAxisProps,
  Label,
} from 'recharts';
import { token } from '@atlaskit/tokens';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';

import { DataPointsEventsTotals, DataPointWithEvents, InterimResults } from '@/ui';
import {
  EXPANDED_GRAPH_HEIGHT,
  EXPANDED_GRAPH_WIDTH,
} from '@/components/composed/new-slo-views/common/constants';
import {
  BASE_FONT_SIZE,
  colors,
  cursorPointer,
  layers,
  loadingColumnStyles,
  positionRelative,
  spaceTopRelaxed,
} from '@/styles';
import { formatDateForDisplay } from '@/utils';
import { COMPLIANCE_PERIOD_IN_DAYS, DateDisplayFormats } from '@/constants';
import Dot from '@/components/composed/new-slo-views/expanded/graph/dot';
import { StickyCustomTooltip } from '@/components/composed/slo-card/graph/tooltip';
import { magnitudeFormatter } from '@/components/composed/new-slo-views/expanded/graph/utils';
// eslint-disable-next-line max-len
import ReferenceLineLabel from '@/components/composed/new-slo-views/expanded/graph/reference-line-label';
import { callbackErrorBudgetGraphSelectGraphDateEvent } from '@/common/ufo/experiences';
import { selectedDateLabels } from '@/utils/date';
import ScrollOffsetAnchor from '@/components/primitive/scroll-offset-anchor';
import { Theme, ThemeContext } from '@/controllers';

import { GraphEventSource } from './graph-tabs';

// const GOOD_BAR_COLOR = 'rgba(31,132,90,0.5)'; // color.background.success.bold
const GOOD_BAR_COLOR = 'rgba(49,161,113,0.8)';
const BAD_BAR_COLOR = 'rgba(202,53,33,0.6)'; // color.background.danger.bold
const GOOD_BAR_WITH_BAD_COUNT_COLOR = 'rgba(175,225,203,0.8)';

const renderBlackLegendText = (value: string) => (
  <span style={{ color: token('color.text', colors.N800) }}>{value}</span>
);

const renderErrorBudgetAxisTick = ({ x, y, payload }: { x: number; y: number; payload: any }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={BASE_FONT_SIZE * -1} // shift the label away from the axis for readability because the bar overlaps the labels: https://github.com/recharts/recharts/issues/2091
      textAnchor="end"
      fill={token('color.text.subtlest', colors.N300)}
      fontSize={BASE_FONT_SIZE}
    >
      {magnitudeFormatter(payload.value)}%
    </text>
  </g>
);

const renderDateAxisTick = ({ x, y, payload }: { x: number; y: number; payload: any }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      y={BASE_FONT_SIZE * 1.5} // shift the label away from the axis for readability
      textAnchor="start"
      fill={token('color.text.subtlest', colors.N300)}
      fontSize={BASE_FONT_SIZE}
    >
      {formatDateForDisplay(payload.value, DateDisplayFormats.NUMERIC_DATE_WITH_MONTH)}
    </text>
  </g>
);

const renderDailyCountAxisTick = ({ x, y, payload }: { x: number; y: number; payload: any }) => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={BASE_FONT_SIZE * 2} // shift the label away from the axis for readability
      textAnchor="start"
      fill={token('color.text.subtlest', colors.N300)}
      fontSize={BASE_FONT_SIZE}
    >
      {/* {new Intl.NumberFormat('en-AU', { maximumSignificantDigits: 3 }).format(payload.value)} */}
      {magnitudeFormatter(payload.value)}
    </text>
  </g>
);

const AXIS_LABEL_MARGIN_LEFT = 15;
const AXIS_LABEL_MARGIN_TOP = -15;

const renderDailyCountLabel = (width: number) => (
  <g transform={`translate(${width + AXIS_LABEL_MARGIN_LEFT},${AXIS_LABEL_MARGIN_TOP})`}>
    <text
      textAnchor="start"
      dominantBaseline="central"
      fill={token('color.text.subtle', colors.N300)}
    >
      Counts
    </text>
  </g>
);

const renderErrorBudgetLabel = () => (
  <g transform={`translate(6,${AXIS_LABEL_MARGIN_TOP})`}>
    <text
      textAnchor="start"
      x={-9}
      dominantBaseline="central"
      fill={token('color.text.subtle', colors.N300)}
    >
      Budget
    </text>
  </g>
);

const PREVIEW_WIDTH = 500;
const PREVIEW_HEIGHT = 200;

const renderEventsBar = (
  width: number,
  height: number,
  viewBox: CartesianViewBox,
  timestamp: number
) => (
  <foreignObject
    height={height}
    width={width} // safari won't render the icons if the width and height are not specified
    x={viewBox.x && viewBox.x - width / 2}
    y={viewBox.y && viewBox.y - height * 4}
  >
    <AtlaskitTooltip
      position="top"
      content={<div>Low Traffic: {selectedDateLabels(new Date(timestamp)).timestampLabel}</div>}
    >
      <div
        css={css`
          border-top-left-radius: 4px;
          border-bottom-left-radius: ${height}px;
          border-top-right-radius: 4px;
          border-bottom-right-radius: ${height}px;
          width: ${width}px;
          height: ${height}px;
          background-color: ${token('color.chart.discovery', colors.N800)};
          &:hover {
            background-color: ${token('color.background.discovery.pressed', colors.N800)};
            transition-duration: 300ms;
          }
        `}
      >
        &nbsp;
      </div>
    </AtlaskitTooltip>
  </foreignObject>
);

const renderInterimLoadingBar = (viewBox: CartesianViewBox, theme: Theme) => {
  const width = 22;
  const height = 237;
  const opacity = theme === Theme.Light ? 0.35 : 0.85;
  return (
    <foreignObject height={height} width={width} x={(viewBox.x ?? 0) - 12} y={(viewBox.y ?? 0) - 1}>
      <div css={loadingColumnStyles(opacity)}>&nbsp;</div>
    </foreignObject>
  );
};

const ErrorBudgetGraph: React.FC<{
  data: DataPointsEventsTotals[];
  errorBudgetData: DataPointWithEvents[];
  errorBudgetDataDomain: YAxisProps['domain'];
  preview?: boolean;
  isResponsive?: boolean;
  selectionCallback?: any;
  showClickPrompt: boolean;
  interimResults?: InterimResults;
  lowTrafficThreshold?: number | null;
  graphEventSource?: GraphEventSource;
}> = ({
  data,
  errorBudgetData,
  errorBudgetDataDomain,
  preview,
  isResponsive,
  selectionCallback,
  showClickPrompt,
  interimResults,
  lowTrafficThreshold,
  graphEventSource,
}) => {
  const refSLOGraph = useRef<HTMLDivElement>(null);
  const selectedPayload = useRef<Array<Payload<number, string>>>([]);
  const { onErrorBudgetGraphSelectGraphDate } = callbackErrorBudgetGraphSelectGraphDateEvent();

  // "preview" applies for the small graph component on the capability SLO list
  const width = preview ? PREVIEW_WIDTH : EXPANDED_GRAPH_WIDTH;
  const height = preview ? PREVIEW_HEIGHT : EXPANDED_GRAPH_HEIGHT;
  const tooltipGap: number = preview ? 5 : 20;
  const tooltipCursorDashSize: number = preview ? 3 : 5;
  const tooltipRestAttributes = preview
    ? {
        position: { y: -50 },
        // To fix the hover issue on the small graph
        // @see https://hello.jira.atlassian.cloud/browse/TOME-2485
        allowEscapeViewBox: { x: true, y: true },
        reverseDirection: { x: true, y: false },
      }
    : { position: { y: -30 } };

  const onSelectedCallback = useCallback(
    (_: any) => {
      if (selectionCallback) {
        selectionCallback(selectedPayload.current);
        onErrorBudgetGraphSelectGraphDate();
        refSLOGraph.current?.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [onErrorBudgetGraphSelectGraphDate, selectionCallback]
  );

  const handleCloseClick = useCallback(
    (e: any) => {
      e.stopPropagation();
      selectedPayload.current = [];
      try {
        selectionCallback([]);
      } catch (_) {
        // @see https://hello.jira.atlassian.cloud/browse/TOME-2590
      }
    },
    [selectionCallback]
  );

  const tooltipContent = useCallback(
    (props: any) => (
      <StickyCustomTooltip
        graphEventSource={graphEventSource}
        active={props.active}
        payload={props.payload}
        stickyPayloadRef={selectedPayload}
        showClickPrompt={showClickPrompt}
      />
    ),
    [showClickPrompt, graphEventSource]
  );

  // Fix the graph updating issue
  // @see https://hello.atlassian.net/browse/PSREAU-1721
  // @credit https://github.com/recharts/recharts/issues/655#issuecomment-617763045
  const [chartKey, updateChart] = useReducer(x => x + 1, 0);
  useEffect(updateChart, [data, errorBudgetData]);

  const [theme] = useContext(ThemeContext);
  return (
    <div css={[positionRelative, spaceTopRelaxed]}>
      <ScrollOffsetAnchor offset={-48} ref={refSLOGraph} />
      {/* The debounce property prevents the (Legend) layout changing between the first and subsequent renders */}
      {/* Why 99% but not 100% here? @see https://github.com/recharts/recharts/issues/172#issuecomment-307858843 */}
      <ResponsiveContainer width={isResponsive ? '99%' : width} height={height} debounce={1}>
        <ComposedChart key={chartKey} data={data} onClick={onSelectedCallback}>
          {/* X Axis */}
          <XAxis
            axisLine={false}
            dataKey="timestamp"
            domain={['auto', data[data.length - 1] && data[data.length - 1].timestamp]}
            scale="time"
            interval={4}
            tick={renderDateAxisTick}
            tickLine={false}
            type="number"
          >
            <Label
              position="left"
              dx={-10}
              dy={8}
              value="Date (UTC)"
              fontSize={11}
              fontWeight={500}
              fill={token('color.text.subtlest')}
            />
          </XAxis>
          {/* Y Axis Left */}
          <YAxis
            axisLine={false}
            dataKey="result"
            orientation="left"
            yAxisId="errorAxis"
            scale="linear"
            tickLine={false}
            tickFormatter={tick => `${tick}%`}
            tick={renderErrorBudgetAxisTick}
            domain={errorBudgetDataDomain}
            label={preview ? () => null : renderErrorBudgetLabel}
          />
          {/* Y Axis Right */}
          <YAxis
            axisLine={false}
            dataKey="totalCount"
            orientation="right"
            yAxisId="dailyCountAxis"
            mirror
            scale="linear"
            tickLine={false}
            domain={[0, (dataMax: number) => dataMax * 1.5]}
            tick={renderDailyCountAxisTick}
            label={() => (preview ? null : renderDailyCountLabel(width))}
          />
          {/* Grid */}
          <CartesianGrid strokeDasharray="3 1" vertical={false} />
          {/* Good count bars */}
          <Bar
            dataKey="goodCount"
            yAxisId="dailyCountAxis"
            stackId="a"
            fill={GOOD_BAR_COLOR}
            name="Good count"
            isAnimationActive={!preview}
          >
            {data?.map((entry: DataPointsEventsTotals) => (
              <Cell
                key={entry.timestamp}
                fill={entry.badCount ? GOOD_BAR_WITH_BAD_COUNT_COLOR : GOOD_BAR_COLOR}
              />
            ))}
          </Bar>
          {/* Bad count bars */}
          <Bar
            dataKey="badCount"
            yAxisId="dailyCountAxis"
            stackId="a"
            fill={BAD_BAR_COLOR}
            name="Bad count"
            isAnimationActive={!preview}
          />
          {/* Error budget trend line */}
          <Line
            animationDuration={550}
            data={errorBudgetData}
            yAxisId="errorAxis"
            name="Error budget (%)"
            dataKey="result"
            stroke={token('color.text', colors.N900)}
            strokeWidth={2}
            dot={<Dot graphEventSource={graphEventSource} />}
            isAnimationActive={!preview}
          />
          {/* Legend titles under the graph */}
          {!preview && (
            <Legend wrapperStyle={{ position: 'relative' }} formatter={renderBlackLegendText} />
          )}
          {/* Low traffic reference line */}
          {lowTrafficThreshold != null && lowTrafficThreshold > 0 && (
            <>
              <Line
                yAxisId="errorAxis"
                name="Low traffic threshold"
                stroke={token('color.chart.discovery', colors.N900)}
                strokeDasharray="3 3"
              />
              <ReferenceLine
                name="Low traffic threshold"
                className="label"
                y={lowTrafficThreshold / 28}
                strokeWidth={2}
                stroke={token('color.chart.discovery', colors.R300)}
                strokeDasharray="3 3"
                yAxisId="dailyCountAxis"
              />
            </>
          )}
          {/* Summary tooltip panel with mouse move on graph */}
          <Tooltip
            cursor={{
              stroke: token('color.text.subtlest'),
              strokeDasharray: `${tooltipCursorDashSize} ${tooltipCursorDashSize}`,
            }}
            content={tooltipContent}
            animationDuration={150}
            wrapperStyle={{
              zIndex: layers.tooltip(),
              visibility: 'visible',
              pointerEvents: 'none',
              paddingLeft: `${tooltipGap}px`,
              paddingRight: `${tooltipGap}px`,
              opacity: 0.9,
            }}
            // https://hello.atlassian.net/browse/PSREAU-1462 we manually add in a datapoint for today
            // with undefined good/bad counts. we don't want Recharts to filter it out
            filterNull={false}
            {...tooltipRestAttributes}
          />
          {/* Budget empty dashed red line */}
          <ReferenceLine
            className="label"
            y={0}
            strokeWidth={2}
            stroke={token('color.text.danger', colors.R300)}
            strokeDasharray="3 3"
            yAxisId="errorAxis"
            label={(props: LabelProps) => (
              <ReferenceLineLabel
                value="Budget Empty"
                applyAxisPadding
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
              />
            )}
          />
          {/* Low traffic day interval indicators */}
          {data?.map(datum => {
            const { isLowTraffic, timestamp } = datum;

            // events bar dimensions: width and height
            // we use large SLO graph on SLO page, and small SLO graph on other
            // pages with SLOs list. The size is adjusted accordingly.
            const isLargeGraph = width > 700;
            // For each bar segment, the width is calculated by the total width
            // of the graph, divided by the number of displaying days in the
            // graph, then minus the padding space of the bar.
            // 50px: this is the fixed offset of bar chart space on the left
            // side of the graph.
            // 3px / 2px: this is the padding space of the bar, adjusted depends
            // on the size of the graph.
            // The height of the bar is either 5px or 4px, adjusted depends on
            // the size of the graph.
            const segmentWidth: number =
              Math.floor((width - 50) / (COMPLIANCE_PERIOD_IN_DAYS + 1)) - (isLargeGraph ? 3 : 2);
            const segmentHeight: number = isLargeGraph ? 5 : 4;

            return (
              <ReferenceLine
                key={timestamp}
                yAxisId="errorAxis"
                isFront
                strokeWidth={0}
                x={timestamp}
              >
                {isLowTraffic ? (
                  <Label
                    content={({ viewBox }) =>
                      renderEventsBar(
                        segmentWidth,
                        segmentHeight,
                        viewBox as CartesianViewBox,
                        timestamp
                      )
                    }
                    position="top"
                    value="Low Traffic"
                    y={0}
                  />
                ) : null}
              </ReferenceLine>
            );
          })}
          {/* Position cursor when clicking on daily bar */}
          {selectedPayload?.current && selectedPayload.current[0] && (
            <ReferenceLine
              isFront
              x={(selectedPayload.current[0].payload as DataPointWithEvents).timestamp}
              stroke={token('color.text.subtlest', 'grey')}
              yAxisId="errorAxis"
              pointerEvents="all"
            >
              <Label
                content={({ viewBox }: any) => (
                  <foreignObject height={24} width={24} x={viewBox.x - 12} y={viewBox.y - 12}>
                    <div onClick={handleCloseClick} css={cursorPointer}>
                      <CrossCircleIcon
                        label="Deselect daily bar"
                        primaryColor={token('color.icon.accent.gray')}
                      />
                    </div>
                  </foreignObject>
                )}
                position="top"
                value="Selected"
                y={0}
              />
            </ReferenceLine>
          )}
          {!preview && interimResults?.isLoading && (
            <ReferenceLine
              key="interimLoadingBar"
              yAxisId="errorAxis"
              isFront
              strokeWidth={0}
              x={data[data.length - 1].timestamp}
            >
              <Label
                content={({ viewBox }) =>
                  renderInterimLoadingBar(viewBox as CartesianViewBox, theme)
                }
              />
            </ReferenceLine>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ErrorBudgetGraph;
